import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { User, Lock, ArrowRight, Loader2, Users, KeyRound, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const StudentLogin = () => {
  const navigate = useNavigate();
  const { loginStudent } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [resetData, setResetData] = useState({ username: '', teacher_secret_answer: '', new_password: '', confirm_password: '' });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleResetChange = (e) => setResetData({ ...resetData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.password) { toast.error('يرجى ملء جميع الحقول'); return; }
    setLoading(true);
    const result = await loginStudent(formData.username, formData.password);
    setLoading(false);
    if (result.success) {
      const status = result.data.student?.status;
      if (status === 'approved') { toast.success('تم تسجيل الدخول بنجاح'); navigate('/student/exams'); }
      else if (status === 'pending') { toast.info('حسابك قيد المراجعة'); navigate('/student/pending'); }
      else if (status === 'rejected') { toast.error('تم رفض طلب التسجيل'); navigate('/student/pending'); }
    } else { toast.error(result.error || 'فشل تسجيل الدخول'); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetData.username || !resetData.teacher_secret_answer || !resetData.new_password) { toast.error('يرجى ملء جميع الحقول'); return; }
    if (resetData.new_password !== resetData.confirm_password) { toast.error('كلمة المرور غير متطابقة'); return; }
    if (resetData.new_password.length < 6) { toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل'); return; }
    setResetLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/student/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: resetData.username, teacher_secret_answer: resetData.teacher_secret_answer, new_password: resetData.new_password }),
      });
      const data = await response.json();
      if (response.ok) { toast.success('تم تغيير كلمة المرور بنجاح'); setShowForgotPassword(false); setResetData({ username: '', teacher_secret_answer: '', new_password: '', confirm_password: '' }); }
      else { toast.error(data.detail || 'فشل تغيير كلمة المرور'); }
    } catch { toast.error('حدث خطأ أثناء تغيير كلمة المرور'); }
    setResetLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FFFFFF]">
      <main className="flex flex-1">
        {/* Right Side - Logo Background with Blur */}
        <div className="hidden lg:flex w-1/2 relative overflow-hidden bg-[#FDF8F0]">
          {/* Blurred Logo Background */}
          <div className="absolute inset-0">
            <img src="/logo.png" alt="background" className="w-full h-full object-cover scale-110 blur-sm opacity-30" />
          </div>
          
          {/* Light Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#FDF8F0]/90 via-[#FDF8F0]/70 to-[#FDF8F0]/90"></div>
          
          {/* Amber Glow Effects */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(239,159,39,0.15),transparent_70%)]"></div>

          {/* Content */}
          <div className="relative z-10 flex flex-col items-center justify-center w-full text-center space-y-8 p-8">
            {/* Clear Logo */}
            <div className="w-48 h-48 rounded-3xl overflow-hidden border-2 border-[#E8DFCF] shadow-[0_0_60px_rgba(239,159,39,0.3)]">
              <img src="/logo.png" alt="Logo" className="w-full h-full object-cover" />
            </div>
            
            <h1 className="text-5xl font-extrabold text-[#412402] drop-shadow-sm">
              المعتمد في اللغة العربية
            </h1>

            <p className="text-[#6B4C2A] text-xl">
              منصة تعليمية متخصصة في اللغة العربية
            </p>

            <div className="w-40 h-1 bg-gradient-to-r from-transparent via-[#EF9F27] to-transparent"></div>
          </div>
        </div>

        {/* Left Side - Login Form */}
        <div className="w-full lg:w-1/2 relative overflow-hidden flex items-center justify-center p-8 bg-[#FFFFFF]">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_30%,rgba(239,159,39,0.05),transparent_60%)]"></div>

          <Card className="relative z-10 w-full max-w-md bg-[#FDF8F0] border border-[#E8DFCF] shadow-xl">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 bg-[#EF9F27]/10 border border-[#E8DFCF] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-[#EF9F27]" />
              </div>
              <CardTitle className="text-2xl font-bold text-[#412402]">دخول الطالب</CardTitle>
              <CardDescription className="text-[#6B4C2A]">ادخل بياناتك للوصول إلى الاختبارات</CardDescription>
            </CardHeader>

            <CardContent className="space-y-5">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#412402]">اسم المستخدم</Label>
                  <div className="relative">
                    <User className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8B7355]" />
                    <Input name="username" value={formData.username} onChange={handleChange}
                      className="pr-10 bg-[#FFFFFF] border-[#E8DFCF] text-[#412402] placeholder:text-[#8B7355] focus:border-[#EF9F27] focus:ring-[#EF9F27]/20"
                      data-testid="student-username-input" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#412402]">كلمة المرور</Label>
                  <div className="relative">
                    <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8B7355]" />
                    <Input type="password" name="password" value={formData.password} onChange={handleChange}
                      className="pr-10 bg-[#FFFFFF] border-[#E8DFCF] text-[#412402] placeholder:text-[#8B7355] focus:border-[#EF9F27] focus:ring-[#EF9F27]/20"
                      data-testid="student-password-input" />
                  </div>
                </div>

                <Button type="submit" className="w-full bg-[#EF9F27] hover:bg-[#D88E1F] text-[#854F0B] h-12 text-base font-bold shadow-[0_4px_15px_rgba(239,159,39,0.3)]" disabled={loading} data-testid="student-login-submit-btn">
                  {loading ? (<><Loader2 className="w-5 h-5 ml-2 animate-spin" />جاري تسجيل الدخول...</>) : (<>دخول<ArrowRight className="w-5 h-5 mr-2" /></>)}
                </Button>

                <button type="button" onClick={() => setShowForgotPassword(true)} className="w-full text-sm text-[#6B4C2A] hover:text-[#EF9F27] transition-colors" data-testid="student-forgot-password-btn">نسيت كلمة المرور؟</button>
              </form>

              <div className="text-center pt-4 border-t border-[#E8DFCF]">
                <p className="text-[#6B4C2A]">ليس لديك حساب؟{' '}<Link to="/student/register" className="text-[#EF9F27] font-medium hover:underline" data-testid="register-link">إنشاء حساب جديد</Link></p>
              </div>

              
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-[#412402]/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-[#FDF8F0] border border-[#E8DFCF] shadow-2xl">
            <CardHeader>
              <CardTitle className="text-xl text-[#412402] flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#EF9F27]" />استعادة كلمة المرور</CardTitle>
              <CardDescription className="text-[#6B4C2A]">أدخل اسم المستخدم وكود التفعيل</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2"><Label className="text-[#412402]">اسم المستخدم</Label><Input name="username" value={resetData.username} onChange={handleResetChange} className="bg-[#FFFFFF] border-[#E8DFCF] text-[#412402]" /></div>
                <div className="space-y-2"><Label className="text-[#412402]">كود التفعيل</Label><Input name="teacher_secret_answer" value={resetData.teacher_secret_answer} onChange={handleResetChange} placeholder="تواصل مع معلمك للحصول على كود التفعيل" className="bg-[#FFFFFF] border-[#E8DFCF] text-[#412402] placeholder:text-[#8B7355]" /></div>
                <div className="space-y-2"><Label className="text-[#412402]">كلمة المرور الجديدة</Label><Input name="new_password" type="password" value={resetData.new_password} onChange={handleResetChange} className="bg-[#FFFFFF] border-[#E8DFCF] text-[#412402]" /></div>
                <div className="space-y-2"><Label className="text-[#412402]">تأكيد كلمة المرور</Label><Input name="confirm_password" type="password" value={resetData.confirm_password} onChange={handleResetChange} className="bg-[#FFFFFF] border-[#E8DFCF] text-[#412402]" /></div>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => { setShowForgotPassword(false); setResetData({ username: '', teacher_secret_answer: '', new_password: '', confirm_password: '' }); }} className="flex-1 border-[#E8DFCF] text-[#412402] hover:bg-[#FDF8F0]">إلغاء</Button>
                  <Button type="submit" className="flex-1 bg-[#EF9F27] hover:bg-[#D88E1F] text-[#854F0B] font-bold" disabled={resetLoading}>{resetLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'تغيير كلمة المرور'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default StudentLogin;
